import {
    type CreateExtArgs,
    type CreateExtensionArgument,
    type DalValue,
    type DmApis,
    type Extension,
    type ExtensionAPI,
    type ExtensionPublicAPI,
    pointerUtils,
    type ValidateValue
} from '@wix/document-manager-core'
import type {CompRef, Pointer, SerializedCompStructure, SingleLayoutData} from '@wix/document-services-types'
import _ from 'lodash'
import {DATA_TYPES, FORWARD_REF_COMPONENT_TYPE, VIEW_MODES} from '../../constants/constants'
import type {ComponentsExtensionAPI} from '../components/components'
import type {DataModelExtensionAPI} from '../dataModel/dataModel'
import type {GlobalComponentsExtensionAPI} from '../globalComponents/globalComponents'
import type {StructureExtensionAPI} from '../structure'
import type {VariantsExtensionAPI} from '../variants/variants'

export interface ForwardRefAPI {
    getReferencedComponent(compPointer: CompRef): CompRef
    getForwardRefToAddRef(globalCompPointer: CompRef, containerPointer: CompRef): CompRef
    add(
        componentToAddRef: CompRef,
        globalCompPointer: CompRef,
        containerPointer: CompRef,
        layout?: Partial<SingleLayoutData>
    ): void
}

export type ForwardRefExtensionAPI = ExtensionAPI & {
    forwardRef: ForwardRefAPI
}

const FORWARD_REF_DATA_TYPE = 'ForwardRef'

const createExtension = ({}: CreateExtensionArgument): Extension => {
    const createExtensionAPI = (createExtArgs: CreateExtArgs): ForwardRefExtensionAPI => {
        const {extensionAPI} = createExtArgs
        const {dataModel} = extensionAPI as DataModelExtensionAPI
        const {components} = extensionAPI as ComponentsExtensionAPI
        const {globalComponents} = extensionAPI as GlobalComponentsExtensionAPI
        const {variants} = extensionAPI as VariantsExtensionAPI
        const structureComponents = (extensionAPI as StructureExtensionAPI).components

        const getReferencedComponent = (compPointer: CompRef): CompRef => {
            const compType = structureComponents.getComponentType(compPointer)
            if (compType !== FORWARD_REF_COMPONENT_TYPE) {
                throw new Error('Component is not a ForwardRef')
            }

            const dataItem = dataModel.components.getItem(compPointer, DATA_TYPES.data)
            if (!dataItem?.rootCompId) {
                throw new Error('ForwardRef component does not have a root component id')
            }
            return pointerUtils.getPointer(dataItem.rootCompId, compPointer.type) as CompRef
        }

        const getForwardRefToAddRef = (globalCompPointer: CompRef, containerPointer: CompRef): CompRef =>
            components.getComponentToAddRef(containerPointer.type)

        const add = (
            componentToAddRef: CompRef,
            globalCompPointer: CompRef,
            containerPointer: CompRef,
            layout?: Partial<SingleLayoutData>
        ): void => {
            if (!globalComponents.isGlobalComponent(globalCompPointer)) {
                throw new Error(
                    'ForwardRef can only point to a global component. The given component is not in the master page.'
                )
            }

            const defaultStructure = components.buildDefaultComponentStructure(FORWARD_REF_COMPONENT_TYPE)
            const forwardRefStructure = {
                ...defaultStructure,
                data: {...defaultStructure.data, rootCompId: globalCompPointer.id}
            } as SerializedCompStructure
            components.addComponentV2(componentToAddRef, containerPointer, forwardRefStructure)
            if (layout) {
                variants.updateComponentDataConsideringVariants(componentToAddRef, layout, DATA_TYPES.layout)
            }
        }

        return {
            forwardRef: {
                getReferencedComponent,
                getForwardRefToAddRef,
                add
            }
        }
    }

    const createValidator = ({extensionAPI}: DmApis): Record<string, ValidateValue> => {
        const {globalComponents} = extensionAPI as GlobalComponentsExtensionAPI

        return {
            validateForwardRefTargetsGlobalComponent: (pointer: Pointer, value: DalValue) => {
                if (pointer.type !== DATA_TYPES.data || value?.type !== FORWARD_REF_DATA_TYPE) {
                    return
                }

                if (_.isEmpty(value.rootCompId)) {
                    return [
                        {
                            shouldFail: true,
                            type: 'forwardRefMissingRootCompId',
                            message: 'ForwardRef data must define rootCompId',
                            extras: {pointer}
                        }
                    ]
                }

                const rootCompId = value.rootCompId as string
                const targetPointer = pointerUtils.getPointer(rootCompId, VIEW_MODES.DESKTOP) as CompRef

                if (!globalComponents.isGlobalComponent(targetPointer)) {
                    return [
                        {
                            shouldFail: true,
                            type: 'forwardRefMustReferenceGlobalComponent',
                            message: 'ForwardRef references a component that is not a global component',
                            extras: {pointer, rootCompId}
                        }
                    ]
                }

                return
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis): ExtensionPublicAPI => {
        const {forwardRef} = extensionAPI as ForwardRefExtensionAPI
        return {
            forwardRef: {
                getReferencedComponent(compPointer: CompRef): CompRef {
                    return forwardRef.getReferencedComponent(compPointer)
                },
                getForwardRefToAddRef(globalCompPointer: CompRef, containerPointer: CompRef): CompRef {
                    return forwardRef.getForwardRefToAddRef(globalCompPointer, containerPointer)
                },
                add(
                    componentToAddRef: CompRef,
                    globalCompPointer: CompRef,
                    containerPointer: CompRef,
                    layout?: Partial<SingleLayoutData>
                ): void {
                    forwardRef.add(componentToAddRef, globalCompPointer, containerPointer, layout)
                }
            }
        }
    }

    return {
        name: 'forwardRef',
        dependencies: new Set(['structure', 'dataModel', 'components', 'globalComponents']),
        createExtensionAPI,
        createValidator,
        createPublicAPI
    }
}

export {createExtension}
